import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { authClient } from '@/api/auth';
import api from '@/api/client';
import type { AuthUser } from '@/types';

export function useAuth() {
  const { user, isLoading, setUser, setLoading } = useAuthStore();

  useEffect(() => {
    authClient.getSession()
      .then(async (session) => {
        const u = (session?.data?.user) as any;
        if (!u) {
          setUser(null);
          return;
        }
        // Use the backend's EFFECTIVE role. On dev environments this honours a
        // "view as" impersonation set in Dev Tools (the authenticate middleware
        // substitutes the role), so the whole UI reflects exactly what that role
        // sees. In production there's no impersonation, so it's just the real
        // role. Falls back to the session role if the lookup fails.
        let role = (u.role ?? 'staff') as AuthUser['role'];
        try {
          const me = await api.get('/auth-extra/me');
          if (me?.data?.user?.role) role = me.data.user.role;
        } catch { /* keep session role */ }
        setUser({ id: u.id, email: u.email, name: u.name, role } as AuthUser);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { user, isLoading };
}

export function useIsAdmin() {
  const user = useAuthStore((s) => s.user);
  return user?.role === 'admin';
}

/** True for admin and director roles. Directors have access to everything
 *  except system tools (restart, Xero connection) and activity logs. */
export function useIsDirector() {
  const user = useAuthStore((s) => s.user);
  return user?.role === 'director' || user?.role === 'admin';
}

/** True for admin, director, and management roles (blocks staff only). */
export function useIsManagement() {
  const user = useAuthStore((s) => s.user);
  return user?.role === 'management' || user?.role === 'director' || user?.role === 'admin';
}

export function useIsStaff() {
  const user = useAuthStore((s) => s.user);
  return user?.role === 'staff';
}
